/* eslint-disable dot-notation */
import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Dropdown from "@shared/v2/Dropdown/Dropdown";
import Avatar from "@shared/v2/Avatar";
import TextLink from "@shared/v2/TextLink";
import CircleInfoSolidV6 from "@shared/v2/Icomoon/Icons/CircleInfoSolidV6";
import Tooltip from "@shared/v2/Tooltip";
import { LeadAssignInfo } from "./shared";
import ActivitiesDropdown from "../../../Reports/components/Leaderboards/ActivitiesDropdown";

const getFallbackOptions = (assignType) =>
  Object.entries(LeadAssignInfo?.[assignType]?.["fallbackOptions"]).map(([key, value]) => ({
    value: key,
    label: value.pretty,
  }));

const formatOptionLabel = ({ label, avatarUrl }) => (
  <div className="tw-flex tw-flex-row tw-items-center tw-whitespace-nowrap">
    <Avatar className="tw-mr-8px" size="small" src={avatarUrl} alt={label} />
    <span className="tw-overflow-ellipsis tw-w-[90px] tw-font-normal">{label}</span>
  </div>
);

const FallBackOptions = ({
  assignType,
  fallback,
  setFallback,
  selectedFallbackUser,
  setSelectedFallbackUser,
  callIndividualOptions,
}) => {
  const [options, setOptions] = useState([]);
  const [activity, setActivity] = useState(null);

  const userOptions = useMemo(
    () => callIndividualOptions.toSorted((a, b) => (a.label > b.label ? 1 : -1)),
    [callIndividualOptions],
  );

  useEffect(() => {
    setOptions(getFallbackOptions(assignType));
  }, [assignType]);

  useEffect(() => {
    setActivity(getFallbackOptions(assignType).find(({ value }) => value === fallback));
  }, [assignType, fallback]);

  return (
    <div className="tw-mt-[24px] tw-flex tw-flex-col tw-bg-[#FAFAFA] tw-rounded-8px tw-border-[1px] tw-border-[#EBEBEB] tw-border-solid tw-p-16px tw-gap-16px">
      <div className="tw-flex tw-flex-row tw-gap-8px tw-text-14px tw-text-gray-75">
        <span className="tw-font-semibold">Fall Back Options</span>
      </div>
      <div className="tw-flex tw-flex-row tw-gap-8px tw-text-14px tw-text-gray-75">
        <span>
          {assignType === "first_to_claim"
            ? "If no one answers, then"
            : "If original agent doesn't answer, then"}
        </span>
        <ActivitiesDropdown activity={activity} onActivityChosen={setFallback} options={options} />
      </div>
      {fallback === "call_individual" && assignType === "first_to_claim" && (
        <>
          <div className="tw-flex tw-flex-col tw-gap-8px ">
            <Dropdown
              label={
                <>
                  Select who will be called
                  <Tooltip
                    className="tw-ml-[5px]"
                    placement="top"
                    trigger={<CircleInfoSolidV6 className="tw-text-gray-75" size="m" />}
                    content="Calls can be routed to any admin user. "
                  />
                </>
              }
              isRequired
              placeholder="Select"
              value={selectedFallbackUser}
              options={userOptions}
              onChange={(e) => setSelectedFallbackUser(e)}
              formatOptionLabel={formatOptionLabel}
              menuShouldComeToFront
            />
          </div>
          <div>Then go to voicemail</div>
        </>
      )}
      {LeadAssignInfo?.[assignType]?.["fallbackOptions"]?.[fallback]?.description && (
        <span>
          <span className="tw-text-14px tw-text-gray-75 tw-italic">
            {LeadAssignInfo?.[assignType]?.["fallbackOptions"]?.[fallback]?.description}
          </span>
          <TextLink
            size="medium"
            className="!tw-no-underline"
            onClick={() =>
              window.open(
                "https://www.brivityknowledge.com/docs/lead-routing-1?highlight=lead%20routing",
                "_blank",
              )
            }
          >
            Learn more
          </TextLink>
        </span>
      )}
    </div>
  );
};

FallBackOptions.propTypes = {
  assignType: PropTypes.string.isRequired,
  fallback: PropTypes.string.isRequired,
  setFallback: PropTypes.func.isRequired,

  selectedFallbackUser: PropTypes.shape({}).isRequired,
  callIndividualOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  setSelectedFallbackUser: PropTypes.func.isRequired,
};

export default FallBackOptions;
